import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  listContributions,
  createContribution,
  updateContribution,
  deleteContribution,
  exportContributionsCsvUrl,
  getContributorSummary,
  getContributorNames,
} from "../../core/api/contributionApi";
import { axiosClient } from "../../core/api/axiosClient";

const PAGE_SIZE = 10;

const emptyForm = {
  contributor_name: "",
  contribution_date: "",
  reason: "",
  contribution_type: "OTHER",
  amount: "",
  mode_of_contribution: "OTHER",
  remarks: "",
  reference_no: "",
  branch: "",
};

function Modal({ open, title, onClose, children, widthClass = "max-w-3xl" }) {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-[2px] p-4"
      onMouseDown={onClose}
    >
      <div
        className={`w-full ${widthClass} max-h-[85vh] overflow-hidden rounded-2xl bg-white shadow-2xl`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-1 text-sm text-gray-600 hover:bg-gray-100"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="max-h-[calc(85vh-68px)] overflow-y-auto px-5 py-4">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function AdminContributions() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);

  // ✅ Summary + contributors list (NEW)
  const [summary, setSummary] = useState(null);
  const [contributors, setContributors] = useState([]);
  const [selectedContributor, setSelectedContributor] = useState(""); // card selection
  const [addNewContributor, setAddNewContributor] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(""); // ACTIVE/CANCELLED
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [ordering, setOrdering] = useState("-contribution_date");

  // Modals
  const [openAdd, setOpenAdd] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const [activeItem, setActiveItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / PAGE_SIZE)),
    [count]
  );

  // ✅ Query params
  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: PAGE_SIZE,
      ordering,
    };

    // card click sets selectedContributor — we use search param to filter by name
    const effectiveSearch = selectedContributor ? selectedContributor : search;

    if (effectiveSearch) params.search = effectiveSearch;
    if (status) params.status = status;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;

    return params;
  }, [page, search, selectedContributor, status, fromDate, toDate, ordering]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await listContributions(queryParams);
      if (Array.isArray(data)) {
        setRows(data);
        setCount(data.length);
      } else {
        setRows(data.results || []);
        setCount(data.count || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // list data
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [queryParams]);

  // ✅ Load contributors dropdown list once
  useEffect(() => {
    (async () => {
      try {
        const names = await getContributorNames();
        setContributors(Array.isArray(names) ? names : []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  // ✅ Load summary (cards + totals) whenever filters change
  useEffect(() => {
    (async () => {
      try {
        // summary should respect filters (status/date range/search)
        const s = await getContributorSummary(queryParams);
        setSummary(s);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [queryParams]);

  const resetFilters = () => {
    setSearch("");
    setSelectedContributor("");
    setStatus("");
    setFromDate("");
    setToDate("");
    setOrdering("-contribution_date");
    setPage(1);
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setAddNewContributor(false);
    setOpenAdd(true);
  };

  const openViewModal = (item) => {
    setActiveItem(item);
    setOpenView(true);
  };

  const openEditModal = (item) => {
    setActiveItem(item);
    setForm({
      contributor_name: item.contributor_name || "",
      contribution_date: item.contribution_date || "",
      reason: item.reason || "",
      contribution_type: item.contribution_type || "OTHER",
      amount: item.amount ?? "",
      mode_of_contribution: item.mode_of_contribution || "OTHER",
      remarks: item.remarks || "",
      reference_no: item.reference_no || "",
      branch: item.branch || "",
    });
    setAddNewContributor(false);
    setOpenEdit(true);
  };

  const openDeleteModal = (item) => {
    setActiveItem(item);
    setOpenDelete(true);
  };

  const handleCreate = async () => {
    if (!form.contributor_name || !form.contribution_date || !form.reason) {
      alert("Please fill Contributor Name, Date, and Reason.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: form.amount === "" ? null : Number(form.amount),
      };
      await createContribution(payload);
      setOpenAdd(false);
      setPage(1);
      await fetchData();

      // refresh contributor list after creating new contributor
      try {
        const names = await getContributorNames();
        setContributors(Array.isArray(names) ? names : []);
      } catch (e) {
        console.error(e);
      }
    } catch (e) {
      console.error(e);
      alert("Create failed. Check console.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!activeItem?.id) return;

    if (!form.contributor_name || !form.contribution_date || !form.reason) {
      alert("Please fill Contributor Name, Date, and Reason.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: form.amount === "" ? null : Number(form.amount),
      };
      await updateContribution(activeItem.id, payload);
      setOpenEdit(false);
      await fetchData();
    } catch (e) {
      console.error(e);
      alert("Update failed. Check console.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!activeItem?.id) return;
    setSaving(true);
    try {
      await deleteContribution(activeItem.id);
      setOpenDelete(false);
      await fetchData();
    } catch (e) {
      console.error(e);
      alert("Delete failed. Check console.");
    } finally {
      setSaving(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const url = exportContributionsCsvUrl(queryParams);
      const res = await axiosClient.get(url, { responseType: "blob" });

      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "contributions.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error(e);
      alert("CSV export failed.");
    }
  };

  const pageStartIndex = (page - 1) * PAGE_SIZE;

  return (
    <div className="p-6">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contributions</h1>
          <p className="text-sm text-gray-600">
            Manage all contributions (Admin/HR only)
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={downloadCsv}
            className="rounded-lg border bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50"
          >
            Export CSV
          </button>
          <button
            onClick={openAddModal}
            className="rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-900"
          >
            + Add Contribution
          </button>
        </div>
      </div>

      {/* ✅ Global Total Block */}
      {summary && (
        <div className="mb-4 flex items-center justify-between rounded-xl border bg-white p-4">
          <div>
            <div className="text-sm text-gray-500">
              Total Contribution {selectedContributor ? `(${selectedContributor})` : ""}
            </div>
            <div className="text-xl font-bold">₹ {summary.total_amount}</div>
          </div>
          <div className="text-sm text-gray-600">
            Total Records: {summary.total_records}
          </div>
        </div>
      )}

      {/* ✅ Contributor Cards */}
      {summary && Array.isArray(summary.contributors) && summary.contributors.length > 0 && (
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {summary.contributors.map((c) => (
            <div
              key={c.contributor_name}
              onClick={() => {
                setSelectedContributor(c.contributor_name);
                setPage(1);
              }}
              className={`cursor-pointer rounded-xl border bg-white p-4 shadow-sm hover:shadow-md ${
                selectedContributor === c.contributor_name ? "ring-2 ring-black" : ""
              }`}
              title="Click to filter by this contributor"
            >
              <div className="text-sm text-gray-500">Contributor</div>
              <div className="text-lg font-semibold">{c.contributor_name}</div>
              <div className="mt-2 text-sm">
                <span className="font-medium">₹ {c.total_amount || 0}</span>
                <span className="ml-2 text-gray-500">
                  ({c.total_records} records)
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 grid grid-cols-1 gap-3 rounded-xl border bg-white p-4 md:grid-cols-6">
        <input
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black/10 md:col-span-2"
          placeholder="Search by name or reason..."
          value={search}
          onChange={(e) => {
            setSelectedContributor(""); // if user types, remove card filter
            setPage(1);
            setSearch(e.target.value);
          }}
        />

        <select
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black/10"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
        >
          <option value="">All Status</option>
          <option value="ACTIVE">ACTIVE</option>
          <option value="CANCELLED">CANCELLED</option>
        </select>

        <input
          type="date"
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black/10"
          value={fromDate}
          onChange={(e) => {
            setPage(1);
            setFromDate(e.target.value);
          }}
        />

        <input
          type="date"
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black/10"
          value={toDate}
          onChange={(e) => {
            setPage(1);
            setToDate(e.target.value);
          }}
        />

        <select
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black/10"
          value={ordering}
          onChange={(e) => {
            setPage(1);
            setOrdering(e.target.value);
          }}
        >
          <option value="-contribution_date">Newest by Contribution Date</option>
          <option value="contribution_date">Oldest by Contribution Date</option>
          <option value="-created_at">Newest by Created At</option>
          <option value="created_at">Oldest by Created At</option>
        </select>

        <div className="md:col-span-6 flex items-center justify-between">
          {selectedContributor ? (
            <div className="text-sm text-gray-600">
              Filtered by contributor:{" "}
              <span className="font-semibold text-gray-900">
                {selectedContributor}
              </span>{" "}
              <button
                className="ml-2 underline"
                onClick={() => setSelectedContributor("")}
              >
                Clear
              </button>
            </div>
          ) : (
            <div />
          )}

          <button
            onClick={resetFilters}
            className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-4 py-3">Sr No</th>
                <th className="px-4 py-3">Contributor Name</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y">
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-4 py-3">
                      <div className="h-4 w-10 rounded bg-gray-200" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-40 rounded bg-gray-200" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-24 rounded bg-gray-200" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-72 rounded bg-gray-200" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="ml-auto h-8 w-28 rounded bg-gray-200" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-10 text-center text-gray-500">
                    No contributions found.
                  </td>
                </tr>
              ) : (
                rows.map((item, idx) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {pageStartIndex + idx + 1}
                    </td>
                    <td className="px-4 py-3">{item.contributor_name}</td>
                    <td className="px-4 py-3">{item.contribution_date}</td>
                    <td className="px-4 py-3 max-w-[420px] truncate" title={item.reason}>
                      {item.reason}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openViewModal(item)}
                          className="rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
                        >
                          View
                        </button>
                        <button
                          onClick={() => openEditModal(item)}
                          className="rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openDeleteModal(item)}
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
          <div className="text-gray-600">
            Page <span className="font-medium text-gray-900">{page}</span> of{" "}
            <span className="font-medium text-gray-900">{totalPages}</span> • Total{" "}
            <span className="font-medium text-gray-900">{count}</span>
          </div>

          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
            >
              Prev
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ADD MODAL */}
      <Modal
        open={openAdd}
        title="Add Contribution"
        onClose={() => setOpenAdd(false)}
        widthClass="max-w-2xl"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-700">Contributor Name *</label>

            {/* ✅ Dropdown + Add New Contributor */}
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              value={addNewContributor ? "__new__" : form.contributor_name}
              onChange={(e) => {
                if (e.target.value === "__new__") {
                  setAddNewContributor(true);
                  setForm((f) => ({ ...f, contributor_name: "" }));
                } else {
                  setAddNewContributor(false);
                  setForm((f) => ({ ...f, contributor_name: e.target.value }));
                }
              }}
            >
              <option value="">Select Contributor</option>
              {contributors.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
              <option value="__new__">+ Add New Contributor</option>
            </select>

            {addNewContributor && (
              <input
                className="mt-2 w-full rounded-lg border px-3 py-2 text-sm"
                placeholder="Enter new contributor name"
                value={form.contributor_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, contributor_name: e.target.value }))
                }
              />
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">
              Contribution Date *
            </label>
            <input
              type="date"
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              value={form.contribution_date}
              onChange={(e) =>
                setForm((f) => ({ ...f, contribution_date: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Type (optional)</label>
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              value={form.contribution_type}
              onChange={(e) =>
                setForm((f) => ({ ...f, contribution_type: e.target.value }))
              }
            >
              <option value="DONATION">DONATION</option>
              <option value="SUPPORT">SUPPORT</option>
              <option value="SPONSORSHIP">SPONSORSHIP</option>
              <option value="HELP">HELP</option>
              <option value="OTHER">OTHER</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Amount (optional)</label>
            <input
              type="number"
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Mode (optional)</label>
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              value={form.mode_of_contribution}
              onChange={(e) =>
                setForm((f) => ({ ...f, mode_of_contribution: e.target.value }))
              }
            >
              <option value="CASH">CASH</option>
              <option value="UPI">UPI</option>
              <option value="BANK">BANK</option>
              <option value="OTHER">OTHER</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-700">Reason *</label>
            <textarea
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              rows={4}
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-700">Remarks (optional)</label>
            <textarea
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              rows={3}
              value={form.remarks}
              onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
            />
          </div>

          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button
              onClick={() => setOpenAdd(false)}
              className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              onClick={handleCreate}
              className="rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </Modal>

      {/* VIEW MODAL */}
      <Modal
        open={openView}
        title="Contribution Details"
        onClose={() => setOpenView(false)}
        widthClass="max-w-2xl"
      >
        {!activeItem ? (
          <div className="text-sm text-gray-600">No data</div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <div className="text-xs text-gray-500">Contributor</div>
                <div className="font-medium">{activeItem.contributor_name}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Date</div>
                <div className="font-medium">{activeItem.contribution_date}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Type</div>
                <div className="font-medium">{activeItem.contribution_type}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Amount</div>
                <div className="font-medium">{activeItem.amount ?? "-"}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Mode</div>
                <div className="font-medium">{activeItem.mode_of_contribution}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Status</div>
                <div className="font-medium">{activeItem.status}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Reference No</div>
                <div className="font-medium">{activeItem.reference_no || "-"}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Branch</div>
                <div className="font-medium">{activeItem.branch || "-"}</div>
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500">Reason</div>
              <div className="rounded-lg border bg-gray-50 p-3">{activeItem.reason}</div>
            </div>

            <div>
              <div className="text-xs text-gray-500">Remarks</div>
              <div className="rounded-lg border bg-gray-50 p-3">
                {activeItem.remarks || "-"}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <div className="text-xs text-gray-500">Created By</div>
                <div className="font-medium">{activeItem.created_by_name || "-"}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Created At</div>
                <div className="font-medium">{activeItem.created_at}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Updated At</div>
                <div className="font-medium">{activeItem.updated_at}</div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* EDIT MODAL */}
      <Modal open={openEdit} title="Edit Contribution" onClose={() => setOpenEdit(false)} widthClass="max-w-2xl">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-700">Contributor Name *</label>
            <input
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              value={form.contributor_name}
              onChange={(e) =>
                setForm((f) => ({ ...f, contributor_name: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Contribution Date *</label>
            <input
              type="date"
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              value={form.contribution_date}
              onChange={(e) =>
                setForm((f) => ({ ...f, contribution_date: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Type</label>
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              value={form.contribution_type}
              onChange={(e) =>
                setForm((f) => ({ ...f, contribution_type: e.target.value }))
              }
            >
              <option value="DONATION">DONATION</option>
              <option value="SUPPORT">SUPPORT</option>
              <option value="SPONSORSHIP">SPONSORSHIP</option>
              <option value="HELP">HELP</option>
              <option value="OTHER">OTHER</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Amount</label>
            <input
              type="number"
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Mode</label>
            <select
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              value={form.mode_of_contribution}
              onChange={(e) =>
                setForm((f) => ({ ...f, mode_of_contribution: e.target.value }))
              }
            >
              <option value="CASH">CASH</option>
              <option value="UPI">UPI</option>
              <option value="BANK">BANK</option>
              <option value="OTHER">OTHER</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-700">Reason *</label>
            <textarea
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              rows={4}
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
          </div>

          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button
              onClick={() => setOpenEdit(false)}
              className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              onClick={handleUpdate}
              className="rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Update"}
            </button>
          </div>
        </div>
      </Modal>

      {/* DELETE MODAL */}
      <Modal open={openDelete} title="Delete Contribution?" onClose={() => setOpenDelete(false)} widthClass="max-w-md">
        <div className="text-sm text-gray-700">
          Are you sure you want to delete{" "}
          <span className="font-semibold">{activeItem?.contributor_name}</span> contribution?
          <div className="mt-2 text-xs text-gray-500">
            Note: This is a soft delete (status will become CANCELLED).
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => setOpenDelete(false)}
            className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            disabled={saving}
            onClick={handleDelete}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            {saving ? "Deleting..." : "Delete"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
